import { CaretDownIcon, CheckIcon, FunnelIcon } from '@phosphor-icons/react';
import { useEffect, useId, useRef, useState } from 'react';

import { PRACTICE_VIEW_OPTIONS, usePracticeView } from '@pages/PracticePage/model';
import { cn } from '@shared/lib/utils';

export const PracticeViewMenu = () => {
  const { viewMode, setViewMode } = usePracticeView();
  const [isOpen, setIsOpen] = useState(false);
  const menuId = useId();
  const menuRef = useRef<HTMLDivElement | null>(null);

  const currentOption =
    PRACTICE_VIEW_OPTIONS.find((option) => option.id === viewMode) ?? PRACTICE_VIEW_OPTIONS[0];

  useEffect(() => {
    if (isOpen) {
      menuRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        type="button"
        className={cn(
          'flex items-center gap-2 rounded-sm border bg-white px-3 py-2 text-sm font-medium text-foreground shadow-sm',
          'transition-colors hover:bg-slate-50'
        )}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <FunnelIcon className="h-4 w-4 text-muted-foreground" />
        <span>{currentOption.label}</span>
        <CaretDownIcon
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-20 cursor-default"
            aria-label="Close menu"
            onClick={() => setIsOpen(false)}
          />
          <div
            id={menuId}
            role="menu"
            tabIndex={-1}
            ref={menuRef}
            className="absolute right-0 z-30 mt-2 w-56 rounded-xl border border-border/70 bg-white p-1 shadow-lg"
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setIsOpen(false);
              }
            }}
          >
            {PRACTICE_VIEW_OPTIONS.map((option) => {
              const isSelected = option.id === viewMode;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isSelected}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm',
                    'transition-colors hover:bg-slate-100',
                    isSelected ? 'font-medium text-foreground' : 'text-muted-foreground'
                  )}
                  onClick={() => {
                    setViewMode(option.id);
                    setIsOpen(false);
                  }}
                >
                  <span>{option.label}</span>
                  {isSelected ? <CheckIcon className="h-4 w-4 text-blue-600" /> : null}
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
};
